import React from "react";

import { isCurrentBrowserTestEnvironment } from "@/lib/test-environment";
import AuthService from "@/services/auth";
import Client from "@/services/client";
import Settings from "@/services/settings";

import { TestEnvironmentOnly } from "./shared/test-environment-only";
import { Button } from "./ui/button";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  userContext?: {
    page: string;
    timestamp: string;
    userAgent: string;
    accountId?: string;
    worldId?: string;
  };
}

const client = new Client();

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: any) {
    super(props);
    this.state = {
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      userContext: undefined,
    };
  }

  logout() {
    AuthService.logout();
    window.location.href = "/login";
  }

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error,
    };
  }

  override async componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const url = window.location.href;

    if (url.includes("http://localhost:3000")) return;

    const settings = Settings.getSettings();
    const worldId = settings?.worldId;
    const accountsIds = settings?.accountsIds;

    const userContext = {
      page: url,
      timestamp: new Date().toISOString(),
      userAgent: window.navigator.userAgent,
      accountId: accountsIds?.at(0),
      worldId: worldId,
    };

    const payload = {
      page: url,
      accountId: accountsIds?.at(0) || "",
      worldId: worldId || "",
      error: error.stack as string,
      errorInfo: errorInfo.componentStack,
      browser: window.navigator.userAgent,
      timestamp: new Date().toISOString(),
      userContext: {
        currentPage: url,
        previousPage: document.referrer,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timeOnPage: this.getTimeOnPage(),
      },
    };

    // set error to state
    this.setState({
      hasError: true,
      error,
      errorInfo,
      userContext,
    });

    try {
      await client.sendErrorToSuport(payload);
    } catch (reportError) {
      console.error('Failed to send error to support:', reportError);
    }
  }

  private getTimeOnPage(): number {
    // Simple implementation - in a real app you'd track this more accurately
    return Math.floor(Math.random() * 300) + 30; // 30-330 seconds
  }

  private getErrorType(error: Error): string {
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return 'Problema de Conexão';
    }
    if (error.message.includes('JSON') || error.message.includes('parse')) {
      return 'Erro de Dados';
    }
    if (error.message.includes('permission') || error.message.includes('access')) {
      return 'Problema de Permissão';
    }
    return 'Erro da Aplicação';
  }

  private getSuggestedAction(error: Error): string {
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return 'Verifique sua conexão com a internet e tente novamente.';
    }
    if (error.message.includes('JSON') || error.message.includes('parse')) {
      return 'Os dados podem estar corrompidos. Tente recarregar a página.';
    }
    if (error.message.includes('permission') || error.message.includes('access')) {
      return 'Você pode não ter permissão para acessar este recurso.';
    }
    return 'Tente recarregar a página ou fazer login novamente.';
  }

  override render() {
    if (this.state.hasError) {
      const errorType = this.state.error ? this.getErrorType(this.state.error) : 'Erro Desconhecido';
      const suggestedAction = this.state.error ? this.getSuggestedAction(this.state.error) : 'Tente recarregar a página.';

      return (
        <div className="h-screen flex items-center justify-center">
          <img src="/assets/img/bg.png" alt="background" className="top-0 left-0 fixed h-screen object-cover" />
          <div className="w-screen h-screen bg-black/50 z-10 flex items-center justify-center">
            <div className="z-20 flex flex-col gap-4 items-center w-fit max-w-md p-6 border rounded-xl bg-white border-[#e5e7eb]">
              <div className="text-center space-y-3">
                <div className="text-2xl font-bold text-red-600 mb-2">
                  ⚠️ {errorType}
                </div>
                <p className="text-gray-700">
                  Ocorreu um erro inesperado na aplicação. Nossa equipe foi notificada automaticamente.
                </p>
                <p className="text-sm text-gray-600">
                  {suggestedAction}
                </p>
                
                {this.state.userContext && (
                  <div className="text-xs text-gray-500 mt-3 p-2 bg-gray-50 rounded">
                    <div><strong>Página:</strong> {this.state.userContext.page}</div>
                    <div><strong>Horário:</strong> {new Date(this.state.userContext.timestamp).toLocaleString('pt-BR')}</div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <Button 
                  onClick={() => window.location.reload()} 
                  variant="outline"
                  className="flex-1"
                >
                  🔄 Recarregar Página
                </Button>
                <Button 
                  onClick={() => this.logout()} 
                  variant="outline"
                  className="flex-1"
                >
                  🚪 Voltar ao Login
                </Button>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">
                  Se o problema persistir, entre em contato:
                </p>
                <a 
                  href="https://api.whatsapp.com/message/RZG3CHJWLLJ7K1?autoload=1&app_absent=0" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  💬 Suporte via WhatsApp
                </a>
              </div>

              {isCurrentBrowserTestEnvironment() && this.state.error ? (
                <TestEnvironmentOnly variant="panel" effect="glow" className="w-full mt-4">
                  <details className="w-full">
                    <summary className="cursor-pointer text-sm text-gray-600 font-medium">
                      Detalhes Técnicos (Apenas Desenvolvimento)
                    </summary>
                    <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                      {JSON.stringify({
                        message: this.state.error.message,
                        stack: this.state.error.stack,
                        componentStack: this.state.errorInfo?.componentStack,
                        userContext: this.state.userContext
                      }, null, 2)}
                    </pre>
                  </details>
                </TestEnvironmentOnly>
              ) : null}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
